import type { DocumentsStats } from '../../services/api';

/** Each card declares its statuses ONCE; both the displayed count and the
 *  URL filter use that list, so card and filter can never diverge. */
interface CardDef {
  key: string;
  label: string;
  description: string;
  /** Statuses this card represents. Empty = special card (e.g. validated_last_7d
   *  uses a derived counter and a status='validated' filter). */
  statuses: string[];
  /** Selector that pulls the count from the backend stats payload. */
  countFrom: (s: DocumentsStats) => number;
  tone: 'amber' | 'blue' | 'green' | 'red';
  /** Extra URL params to apply on click (e.g. validated_within=7d). */
  extraParams?: Record<string, string>;
}

const CARDS: CardDef[] = [
  {
    key: 'to_validate',
    label: 'À valider',
    description: 'extraits, disponibles',
    statuses: ['extracted'],
    countFrom: (s) => s.to_validate,
    tone: 'amber',
  },
  {
    key: 'pending_validation',
    label: 'En validation',
    description: 'verrouillés par un analyste',
    statuses: ['pending_validation'],
    countFrom: (s) => s.pending_validation,
    tone: 'amber',
  },
  {
    key: 'in_progress',
    label: 'En cours',
    description: 'uploaded ou en extraction',
    statuses: ['uploaded', 'processing'],
    countFrom: (s) => s.in_progress,
    tone: 'blue',
  },
  {
    key: 'validated_last_7d',
    label: 'Validés (7j)',
    description: 'validés ces 7 derniers jours',
    statuses: ['validated'],
    countFrom: (s) => s.validated_last_7d,
    tone: 'green',
    extraParams: { validated_within: '7d' },
  },
  {
    key: 'in_error',
    label: 'En erreur',
    description: 'à diagnostiquer',
    statuses: ['error', 'failed'],
    countFrom: (s) => s.in_error,
    tone: 'red',
  },
];

const TONES: Record<CardDef['tone'], { ring: string; text: string; hover: string }> = {
  amber: { ring: 'ring-amber-200', text: 'text-amber-700', hover: 'hover:bg-amber-50' },
  blue: { ring: 'ring-blue-200', text: 'text-blue-700', hover: 'hover:bg-blue-50' },
  green: { ring: 'ring-green-200', text: 'text-green-700', hover: 'hover:bg-green-50' },
  red: { ring: 'ring-red-200', text: 'text-red-700', hover: 'hover:bg-red-50' },
};

export interface CardClickPayload {
  /** CSV of statuses to set as `?status=...`, or null to clear. */
  statusCsv: string | null;
  /** Extra params to apply alongside (e.g. validated_within). */
  extraParams?: Record<string, string>;
}

interface Props {
  stats: DocumentsStats | null;
  loading: boolean;
  /** Current value of `?status` (CSV) — used to highlight the active card. */
  activeStatusCsv?: string | null;
  onCardClick: (payload: CardClickPayload) => void;
}

export function DocumentsStatsCards({ stats, loading, activeStatusCsv, onCardClick }: Props) {
  const activeKey = (() => {
    if (!activeStatusCsv) return null;
    const active = activeStatusCsv.split(',').map((s) => s.trim()).sort().join(',');
    return CARDS.find((c) => [...c.statuses].sort().join(',') === active)?.key ?? null;
  })();

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
      {CARDS.map((card) => {
        const tone = TONES[card.tone];
        const isActive = activeKey === card.key;
        const value = stats ? card.countFrom(stats) : null;
        const handleClick = () => {
          if (isActive) {
            onCardClick({ statusCsv: null });
          } else {
            onCardClick({
              statusCsv: card.statuses.join(','),
              extraParams: card.extraParams,
            });
          }
        };
        return (
          <button
            key={card.key}
            type="button"
            onClick={handleClick}
            className={`text-left bg-white border rounded-lg p-3 ring-1 ring-inset ${tone.ring} ${tone.hover} transition-colors ${
              isActive ? 'border-gray-800 ring-2' : 'border-gray-200'
            }`}
          >
            <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
              {card.label}
            </div>
            <div className={`text-2xl font-semibold mt-1 ${tone.text}`}>
              {loading || value === null ? '—' : value}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">{card.description}</div>
          </button>
        );
      })}
    </div>
  );
}
